import { getAllUsers } from 'api/user';
import Button from 'components/form/Button';
import { SearchInput } from 'components/form/Input';
import CustomTable from 'components/layouts/CustomTable';
import Loader from 'components/layouts/Loader';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useUserStore, { UserProps } from 'state/userStore';
import UserModal from 'pages/admin/modal/UserModal';

type UserData = UserProps & { sn: number };

const Users = () => {
  window.document.title = 'Users';

  const { user } = useUserStore();

  const [data, setData] = useState<UserData[]>([]);
  const [query, setQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProps | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchList, setSearchList] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchUsers = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await getAllUsers(user.type);

      const users: UserProps[] = res.data;

      setData(
        users.map((user, index) => ({
          ...user,
          sn: index + 1,
        }))
      );
    } catch (error: any) {
      const { errors } = error.response.data;

      errors.forEach((err: { error: string }) => {
        toast.error(error);
      });

      console.log(error);
    }
    setLoading(false);
  };

  const columns = [
    {
      Header: 'S/N',
      accessor: 'sn',
      Cell: ({ row: { original } }: any) => {
        const { sn } = original;
        return <p>{sn}</p>;
      },
    },
    {
      Header: 'Full Name',
      accessor: 'fullName',
      minWidth: '200px',
      Cell: ({ row: { original } }: any) => {
        const { fullName } = original;
        return <p>{fullName}</p>;
      },
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell: ({ row: { original } }: any) => {
        const { email } = original;
        return <p>{email}</p>;
      },
    },
    {
      Header: 'Phone Number',
      accessor: 'phoneNumber',
      Cell: ({ row: { original } }: any) => {
        const { phoneNumber } = original;
        return <p>{phoneNumber.length ? phoneNumber : 'NaN'}</p>;
      },
    },
    {
      Header: 'Country',
      accessor: 'country',
      minWidth: '200px',
      Cell: ({ row: { original } }: any) => {
        const { country } = original;
        return <p>{country}</p>;
      },
    },
    {
      Header: '',
      accessor: 'options',
      disableSortBy: true,
      Cell: ({ row: { original } }: any) => {
        return (
          <Button onClick={() => selectUser(original)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-6 w-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
              />
            </svg>
          </Button>
        );
      },
    },
  ];

  const handleSearch = (searchStr: string) => {
    setQuery(searchStr);
    const studentList = data.filter(
      (item) =>
        item.fullName.toLowerCase().startsWith(searchStr) ||
        item.email.toLowerCase().startsWith(searchStr) ||
        item.country.toLowerCase().startsWith(searchStr)
    );

    setSearchList(studentList);
  };

  const selectUser = (data: UserProps) => {
    setSelectedUser(data);
    setIsOpen(true);
  };

  const handleClose = () => {
    setSelectedUser(null);
    setIsOpen(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <div className="px-5">
      <div className="flex flex-col gap-y-4">
        <h3 className="text-xl uppercase">Users Table</h3>
        <SearchInput
          name="search"
          placeholder="search for users"
          handleChange={(value) => handleSearch(value)}
          value={query}
        />
      </div>
      <br />
      {loading ? (
        <Loader />
      ) : (
        <CustomTable
          data={searchList.length ? searchList : data}
          columnShape={columns}
          size={20}
        />
      )}

      {isOpen && !!selectedUser && (
        <UserModal
          data={selectedUser}
          closeModal={handleClose}
          reload={fetchUsers}
        />
      )}
    </div>
  );
};

export default Users;
